/**
 * Simple in-memory cache implementation with scheduled clearing
 */
class MemoryCache {
  constructor() {
    this.cache = new Map();
    if (typeof window === 'undefined') {
      // Only initialize scheduled clearing on server-side
      this.initializeScheduledClearing();
    }
  }

  /**
   * Initialize scheduled cache clearing
   * - Clears at 15:00 (3 PM) every day
   * - Clears at 00:00 (midnight) every day
   */
  initializeScheduledClearing() {
    const scheduleNextClear = (hour, minute) => {
      const now = new Date();
      const nextClear = new Date(now);
      nextClear.setHours(hour, minute, 0, 0);

      // If the time has already passed today, schedule for tomorrow
      if (now > nextClear) {
        nextClear.setDate(nextClear.getDate() + 1);
      }

      const timeUntilClear = nextClear.getTime() - now.getTime();

      setTimeout(() => {
        this.clear();
        console.log(`Scheduled cache clear executed at ${new Date().toLocaleString()}`);
        // Schedule next clearing
        scheduleNextClear(hour, minute);
      }, timeUntilClear);

      console.log(`Next cache clear scheduled for ${nextClear.toLocaleString()}`);
    };

    // Schedule clearing at 15:00 (3 PM)
    scheduleNextClear(15, 0);
    // Schedule clearing at 00:00 (midnight)
    scheduleNextClear(0, 0);
  }

  /**
   * Get a value from cache
   * @param {string} key - Cache key
   * @param {number} maxAge - Maximum age in milliseconds
   * @returns {any|null} Cached value or null if not found/expired
   */
  get(key, maxAge = 5 * 60 * 1000) { // Default 5 minutes
    const item = this.cache.get(key);
    if (!item) return null;

    const now = Date.now();
    if (now - item.timestamp > maxAge) {
      this.cache.delete(key);
      return null;
    }

    return item.value;
  }

  /**
   * Set a value in cache
   * @param {string} key - Cache key 
   * @param {any} value - Value to cache
   */
  set(key, value) {
    this.cache.set(key, {
      value,
      timestamp: Date.now()
    });
  }

  /**
   * Clear the entire cache
   */
  clear() {
    this.cache.clear();
    console.log(`Cache cleared at ${new Date().toLocaleString()}`);
  }

  /**
   * Delete a specific key from cache
   * @param {string} key - Cache key to delete
   */
  delete(key) {
    this.cache.delete(key);
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      lastCleared: new Date().toLocaleString()
    };
  }
}

// Create singleton instance
const cacheInstance = new MemoryCache();

// For CommonJS (Node.js) environment
if (typeof module !== 'undefined' && module.exports) {
  module.exports = cacheInstance;
}
// For ES modules (browser) environment
else if (typeof exports !== 'undefined') {
  exports.default = cacheInstance;
  exports.get = cacheInstance.get.bind(cacheInstance);
  exports.set = cacheInstance.set.bind(cacheInstance);
  exports.clear = cacheInstance.clear.bind(cacheInstance);
  exports.delete = cacheInstance.delete.bind(cacheInstance);
  exports.getStats = cacheInstance.getStats.bind(cacheInstance);
} 